<script setup lang="ts">
import { ref, watchPostEffect, onBeforeUnmount } from 'vue';

// manual teleport that survives target DOM element recreation.
// Vue's built-in Teleport caches the resolved target element and doesn't
// re-query the selector when the old element is destroyed and a new one
// with the same id is created. this component uses watchPostEffect to
// re-resolve the target after every reactive change + DOM patch.

const props = defineProps<{
	// CSS selector for the target element (e.g. "#panel-content-foo")
	to: string;
	// reactive version counter — triggers re-resolution when tree structure changes
	version: number;
}>();

const wrapperRef = ref<HTMLElement | null>(null);
// hidden host element — keeps the wrapper in DOM when no target is found
// so Vue can still patch the vdom tree
const hostRef = ref<HTMLElement | null>(null);

watchPostEffect(() => {
	// track reactive deps
	const selector = props.to;
	const _v = props.version;
	void _v;

	const wrapper = wrapperRef.value;
	if (!wrapper) return;

	const target = document.querySelector(selector);
	if (target && wrapper.parentElement !== target) {
		target.appendChild(wrapper);
	} else if (!target) {
		// target doesn't exist (yet) — park in hidden host
		const host = hostRef.value;
		if (host && wrapper.parentElement !== host) {
			host.appendChild(wrapper);
		}
	}
});

onBeforeUnmount(() => {
	// move wrapper back to host so Vue can unmount it properly
	const wrapper = wrapperRef.value;
	const host = hostRef.value;
	if (wrapper && host && wrapper.parentElement !== host) {
		host.appendChild(wrapper);
	}
});
</script>

<template>
	<!-- hidden host keeps the wrapper in DOM when target is unavailable -->
	<div ref="hostRef" style="display: none">
		<div ref="wrapperRef">
			<slot />
		</div>
	</div>
</template>
